import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserShield } from '@fortawesome/free-solid-svg-icons';
import Layout from './Layout';
import './Settings.css';
import withAuth from '../utils/withAuth';
import axiosInstance from '../utils/axiosConfig';
import AccountDatePicker from './ui/AccountDatePicker';
import PhoneField, { isValidPhoneNumber } from './ui/PhoneField';
import AvatarPositionEditor from './ui/AvatarPositionEditor';
import logo from '../assets/images/IMT_logo.png';
import { useFlashMessage } from './FlashMessage';
import { getAbsoluteMediaUrl, addCacheBuster } from '../utils/mediaUtils';
import { getErrorMessage } from '../utils/errorMessages';
import Footer from './ui/Footer';
import HamsterFloatingButton from './HamsterFloatingButton';
import BackToTopButton from './BackToTopButton';
import COUNTRIES from '../data/countries.json';
import EmployeeCrmPartnerBlock from './hr/EmployeeCrmPartnerBlock';
import { TEAM_CALENDAR_CONSENT_TEXT } from '../constants/teamCalendarConsent';

/**
 * AdminEditEmployee - Admin page for editing employee data
 * Uses EXACT same layout as AccountInformation
 */
function AdminEditEmployee() {
  const history = useHistory();
  const { id } = useParams();
  const { showSuccess, showError } = useFlashMessage();
  
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  // Employee data state
  const [employee, setEmployee] = useState(null);
  const [formData, setFormData] = useState({
    languagesSpoken: [],
    workingCharacteristics: []
  });
  const [dropdownOptions, setDropdownOptions] = useState({});
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  
  // Form state tracking
  const [hasChanges, setHasChanges] = useState(false);

  // Languages dropdown state
  const [showLanguagesDropdown, setShowLanguagesDropdown] = useState(false);
  const [languageSearch, setLanguageSearch] = useState('');
  const languagesRef = useRef(null);

  // Additional entities dropdown state
  const [showAdditionalOfficesDropdown, setShowAdditionalOfficesDropdown] = useState(false);
  const additionalOfficesRef = useRef(null);
  
  // Avatar upload state
  const [showAvatarOptions, setShowAvatarOptions] = useState(false);
  const [showAvatarEditor, setShowAvatarEditor] = useState(false);
  const [selectedAvatarFile, setSelectedAvatarFile] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarOptionsRef = useRef(null);

  /**
   * Format employee data for display
   */
  const formatEmployeeData = (data) => {
    console.log('🔄 FORMAT_EMPLOYEE: Formatting employee data:', data);
    console.log('🔄 FORMAT_EMPLOYEE: Position in raw data:', data.position);
    console.log('🔄 FORMAT_EMPLOYEE: Office in raw data:', data.office);
    console.log('🔄 FORMAT_EMPLOYEE: Department in raw data:', data.department);
    
    const formatted = {
      ...data,
      displayName: data.preferred_name || `${data.first_name} ${data.last_name}`.trim() || data.email,
      fullName: `${data.first_name || ''} ${data.last_name || ''}`.trim(),
      hasAvatar: !!(data.avatar_photo),
      avatarUrl: getAbsoluteMediaUrl(data.avatar_photo),
      dateOfBirth: data.date_of_birth ? new Date(data.date_of_birth) : null,
      dateJoinedHMS: data.date_joined_hms ? new Date(data.date_joined_hms) : null,
      endDate: data.end_date ? new Date(data.end_date) : null,
      languagesSpoken: data.languages_spoken || [],
      workingCharacteristics: data.working_characteristics || [],
      businessContexts: {
        solidFuels: data.business_solid_fuels,
        liquidFuels: data.business_liquid_fuels
      },
      supervisors: data.supervisors || [],
      heads_of_department: data.heads_of_department || [],
      // Explicitly ensure position, office, and department are preserved
      position: data.position || null,
      office: data.office || null,
      additional_offices: Array.isArray(data.additional_offices) ? data.additional_offices : [],
      department: data.department || null
    };
    
    console.log('✅ FORMAT_EMPLOYEE: Formatted employee:', formatted);
    console.log('✅ FORMAT_EMPLOYEE: Position in formatted:', formatted.position);
    
    return formatted;
  };

  /**
   * Load dropdown options (can be called independently to refresh)
   */
  const loadDropdownOptions = useCallback(async () => {
    console.log('🔄 ADMIN EDIT: Loading dropdown options');
    try {
      const optionsResponse = await axiosInstance.get('/api/position/choices/');
      
      const options = {
        offices: optionsResponse.data.offices || [],
        departments: optionsResponse.data.departments || [],
        positions: optionsResponse.data.positions || [],
        workingCharacteristics: optionsResponse.data.working_characteristics || [],
        languages: optionsResponse.data.languages || []
      };
      
      setDropdownOptions(options);
      console.log('✅ ADMIN EDIT: Dropdown options loaded');
      return options;
    } catch (error) {
      console.error('❌ ADMIN EDIT: Error loading dropdown options:', error);
      // Don't throw - allow the component to continue with existing options
      return dropdownOptions;
    }
  }, []);

  /**
   * Load employee data and dropdown options
   */
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load employee data and dropdown options in parallel
      const [employeeResponse] = await Promise.all([
        axiosInstance.get(`/api/admin/employees/${id}/`),
        loadDropdownOptions()
      ]);
      
      const formattedEmployee = formatEmployeeData(employeeResponse.data);
      // Phone numbers are stored and edited as E.164 (e.g. "+27123456789").
      // PhoneField consumes that value directly — no country-code splitting.

      setEmployee(formattedEmployee);
      setFormData(formattedEmployee);
      // Options already set by loadDropdownOptions
      
    } catch (err) {
      console.error('Error loading employee data:', err);
      showError(getErrorMessage(err, 'Employee update failed. Please try again.'));
      setTimeout(() => history.push('/settings/hms-admin'), 2000);
    } finally {
      setLoading(false);
    }
  }, [id, loadDropdownOptions]);  // Depend on id and loadDropdownOptions

  /**
   * Reload dropdown options when window regains focus
   * This ensures fresh data after system data management edits
   */
  useEffect(() => {
    const handleFocus = () => {
      console.log('🔄 ADMIN EDIT: Window focused, reloading dropdown options');
      loadDropdownOptions();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [loadDropdownOptions]);

  /**
   * Listen for system data updates and reload dropdown options
   * This ensures immediate refresh when positions/offices/departments are modified
   */
  useEffect(() => {
    const handleSystemDataUpdate = (event) => {
      console.log('🔔 ADMIN EDIT: System data updated, reloading dropdown options:', event.detail);
      loadDropdownOptions();
    };
    
    window.addEventListener('systemDataUpdated', handleSystemDataUpdate);
    return () => window.removeEventListener('systemDataUpdated', handleSystemDataUpdate);
  }, [loadDropdownOptions]);

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  /**
   * Handle form field changes
   */
  const handleFieldChange = useCallback((fieldName, value) => {
    setFormData(prev => {
      const updated = { ...prev, [fieldName]: value };
      setHasChanges(true);
      
      setValidationErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        if (newErrors[fieldName]) {
          delete newErrors[fieldName];
        }
        return newErrors;
      });
      
      return updated;
    });
  }, []);

  /**
   * Prepare data for API update
   */
  const prepareUpdateData = useCallback((data) => {
    const updateData = { ...data };
    
    // Handle office and department as IDs
    if (updateData.office && typeof updateData.office === 'object') {
      updateData.office_id = updateData.office.id;
      delete updateData.office;
    }

    // Handle additional entities (exclude main entity from the list)
    if (Array.isArray(updateData.additional_offices)) {
      const mainId = updateData.office_id;
      updateData.additional_office_ids = updateData.additional_offices
        .map(o => (o && typeof o === 'object' ? o.id : o))
        .filter(id => id && id !== mainId);
    }
    delete updateData.additional_offices;

    if (updateData.department && typeof updateData.department === 'object') {
      updateData.department_id = updateData.department.id;
      delete updateData.department;
    }
    
    // Handle position - extract ID if it's an object (backend expects position_id)
    if (updateData.position && typeof updateData.position === 'object') {
      updateData.position_id = updateData.position.id;
      delete updateData.position;
    } else if (updateData.position) {
      // If it's a string code, we need to look up the ID from dropdown options
      const positionOption = dropdownOptions.positions?.find(pos => pos.value === updateData.position);
      if (positionOption) {
        updateData.position_id = positionOption.id;
      }
      delete updateData.position;
    }
    
    // Format date of birth
    if (updateData.dateOfBirth instanceof Date) {
      const year = updateData.dateOfBirth.getFullYear();
      const month = String(updateData.dateOfBirth.getMonth() + 1).padStart(2, '0');
      const day = String(updateData.dateOfBirth.getDate()).padStart(2, '0');
      updateData.date_of_birth = `${year}-${month}-${day}`;
      delete updateData.dateOfBirth;
    }
    
    // Format date joined HMS
    if (updateData.dateJoinedHMS instanceof Date) {
      const year = updateData.dateJoinedHMS.getFullYear();
      const month = String(updateData.dateJoinedHMS.getMonth() + 1).padStart(2, '0');
      const day = String(updateData.dateJoinedHMS.getDate()).padStart(2, '0');
      updateData.date_joined_hms = `${year}-${month}-${day}`;
      delete updateData.dateJoinedHMS;
    }

    // Format end date (empty clears it on the backend)
    if (updateData.endDate instanceof Date) {
      const year = updateData.endDate.getFullYear();
      const month = String(updateData.endDate.getMonth() + 1).padStart(2, '0');
      const day = String(updateData.endDate.getDate()).padStart(2, '0');
      updateData.end_date = `${year}-${month}-${day}`;
    } else {
      updateData.end_date = null;
    }
    delete updateData.endDate;

    // Handle business contexts
    if (updateData.businessContexts) {
      updateData.business_solid_fuels = updateData.businessContexts.solidFuels;
      updateData.business_liquid_fuels = updateData.businessContexts.liquidFuels;
      delete updateData.businessContexts;
    }
    
    // Convert camelCase to snake_case
    if (updateData.languagesSpoken !== undefined) {
      updateData.languages_spoken = updateData.languagesSpoken;
      delete updateData.languagesSpoken;
    }
    
    if (updateData.workingCharacteristics !== undefined) {
      updateData.working_characteristics = updateData.workingCharacteristics;
      delete updateData.workingCharacteristics;
    }
    
    // Clean up display fields
    const fieldsToDelete = [
      'displayName', 'fullName', 'hasAvatar', 'avatarUrl', 'avatar_photo',
      'created_at', 'updated_at', 'email', 'hms_employee_id', 'id', 'is_superuser', 'is_active'
    ];
    
    fieldsToDelete.forEach(field => {
      if (updateData[field] !== undefined) {
        delete updateData[field];
      }
    });
    
    return updateData;
  }, [dropdownOptions]);

  /**
   * Handle save
   */
  const handleSave = async () => {
    try {
      console.log('💾 ADMIN SAVE: Starting save process');
      console.log('💾 ADMIN SAVE: Current formData:', formData);
      console.log('💾 ADMIN SAVE: Position in formData:', formData.position);
      
      setSaving(true);
      setValidationErrors({});
      
      // Phone numbers are already E.164 (PhoneField stores them that way).
      // Validate before sending so bad numbers are caught inline, not as a
      // generic 400 from the backend.
      const phoneErrors = {};
      if (formData.work_phone && !isValidPhoneNumber(formData.work_phone)) {
        phoneErrors.work_phone = ['Enter a valid phone number including country code.'];
      }
      if (formData.personal_phone && !isValidPhoneNumber(formData.personal_phone)) {
        phoneErrors.personal_phone = ['Enter a valid phone number including country code.'];
      }
      if (Object.keys(phoneErrors).length > 0) {
        setValidationErrors((prev) => ({ ...prev, ...phoneErrors }));
        showError('Please correct the errors below');
        setSaving(false);
        return;
      }

      const updateData = prepareUpdateData(formData);
      
      console.log('💾 ADMIN SAVE: Prepared update data:', updateData);
      console.log('💾 ADMIN SAVE: position_id in updateData:', updateData.position_id);
      console.log('💾 ADMIN SAVE: Sending PUT request to /api/admin/employees/' + id + '/');
      
      const response = await axiosInstance.put(`/api/admin/employees/${id}/`, updateData);
      
      console.log('💾 ADMIN SAVE: Response received:', response.data);
      
      if (response.status === 200) {
        const updatedEmployee = formatEmployeeData(response.data);
        // Phones come back as E.164 and are used as-is by PhoneField.
        setEmployee(updatedEmployee);
        setFormData(updatedEmployee);
        setValidationErrors({});
        setHasChanges(false);
        showSuccess('Employee updated successfully!');
      }
    } catch (err) {
      console.error('Error updating employee:', err);
      
      if (err.response?.status === 400 && err.response.data) {
        setValidationErrors(err.response.data);
        showError('Please correct the errors below');
      } else {
        showError(getErrorMessage(err, 'Employee update failed. Please try again.'));
      }
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle working characteristics change
   */
  const handleWorkingCharacteristicsChange = (characteristic, checked) => {
    const current = formData.workingCharacteristics || [];
    let updated;
    
    if (checked) {
      if (!current.includes(characteristic) && current.length < 10) {
        updated = [...current, characteristic];
      } else {
        updated = current;
      }
    } else {
      updated = current.filter(c => c !== characteristic);
    }
    
    handleFieldChange('workingCharacteristics', updated);
  };

  /**
   * Handle multi-select languages change
   */
  const handleLanguagesChange = (selectedLanguages) => {
    handleFieldChange('languagesSpoken', selectedLanguages);
  };

  /**
   * Handle file input change - Show positioning editor
   */
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file
      const maxSize = 5 * 1024 * 1024; // 5MB
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      
      if (!allowedTypes.includes(file.type)) {
        showError('Invalid file type. Please upload JPG, PNG, or GIF images only.');
        return;
      }
      
      if (file.size > maxSize) {
        showError('File size too large. Please upload images smaller than 5MB.');
        return;
      }
      
      setSelectedAvatarFile(file);
      setShowAvatarEditor(true);
    }
    
    e.target.value = '';
  };

  /**
   * Handle avatar editor save
   */
  const handleAvatarEditorSave = async (positionedFile) => {
    setShowAvatarEditor(false);
    setSelectedAvatarFile(null);
    
    await handleAvatarUpload(positionedFile);
  };

  /**
   * Handle avatar editor cancel
   */
  const handleAvatarEditorCancel = () => {
    setShowAvatarEditor(false);
    setSelectedAvatarFile(null);
  };

  /**
   * Handle avatar upload
   */
  const handleAvatarUpload = async (file) => {
    try {
      setUploadingAvatar(true);
      
      const formDataObj = new FormData();
      formDataObj.append('avatar', file);
      
      // Upload - backend needs to handle which employee
      const response = await axiosInstance.post(`/api/admin/employees/${id}/avatar/`, formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      
      if (response.data.success) {
        const baseAvatarUrl = getAbsoluteMediaUrl(response.data.avatar_url);
        const avatarUrl = addCacheBuster(baseAvatarUrl);
        
        const updatedEmployee = { ...employee, avatarUrl: avatarUrl, hasAvatar: true };
        const updatedFormData = { ...formData, avatarUrl: avatarUrl, hasAvatar: true };
        
        setEmployee(updatedEmployee);
        setFormData(updatedFormData);
        showSuccess('Avatar uploaded successfully');
        
        // Preload image
        const img = new Image();
        img.src = avatarUrl;
      } else {
        showError(getErrorMessage({ response }, 'Employee update failed. Please try again.'));
      }
    } catch (err) {
      console.error('Avatar upload error:', err);
      showError(getErrorMessage(err, 'Employee update failed. Please try again.'));
    } finally {
      setUploadingAvatar(false);
    }
  };

  /**
   * Handle file selection
   */
  const handleFileSelection = () => {
    setShowAvatarOptions(false);
    document.getElementById('admin-avatar-upload').click();
  };

  // Handle click outside for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languagesRef.current && !languagesRef.current.contains(event.target)) {
        setShowLanguagesDropdown(false);
        setLanguageSearch('');
      }
      if (additionalOfficesRef.current && !additionalOfficesRef.current.contains(event.target)) {
        setShowAdditionalOfficesDropdown(false);
      }
      if (avatarOptionsRef.current && !avatarOptionsRef.current.contains(event.target)) {
        setShowAvatarOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Show loading state
  if (loading) {
    return (
      <Layout showLeftNavSidebar={true} showRightSidebar={false}>
        <div className="account-information-layout">
          <div className="account-information-container">
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading employee data...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!employee) {
    return null;
  }

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="account-information-layout">
        {/* HMS Header */}
        <div className="account-information-header">
          <div className="header-logo">
            <img src={logo} alt="IMT" className="header-logo-img" />
            <div>
              <h1 className="hms-screen-title">Edit Employee: {employee.displayName}</h1>
              <p className="edit-employee-subtitle">Modify profile data and role assignments</p>
            </div>
          </div>
          <div className="settings-actions">
            <button 
              onClick={() => history.push('/settings/hms-admin')}
              className="cancel-button"
            >
              Back to Admin
            </button>
            {hasChanges && (
              <span className="unsaved-changes">Unsaved changes</span>
            )}
            <button 
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className={`account-save-button ${saving ? 'saving' : ''}`}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
        
        <div className="account-information-content">
          <div className="account-information-container">

            {/* Avatar Position Editor */}
            {showAvatarEditor && selectedAvatarFile && (
              <AvatarPositionEditor
                file={selectedAvatarFile}
                onSave={handleAvatarEditorSave}
                onCancel={handleAvatarEditorCancel}
              />
            )}

            <div className="profile-form">
            {/* Personal Information Section */}
            <div className="form-section">
              <h2>Personal Information</h2>
              
              {/* Avatar Section - Editable */}
              <div className="top-avatar-section">
                <div className="top-avatar-container">
                  <div className="top-avatar-preview">
                    {formData.hasAvatar ? (
                      <img 
                        key={formData.avatarUrl}
                        src={formData.avatarUrl} 
                        alt="Profile avatar" 
                        className="top-avatar-image"
                      />
                    ) : (
                      <div className="top-avatar-placeholder">
                        <span>{formData.displayName?.charAt(0) || 'U'}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="top-avatar-controls" ref={avatarOptionsRef}>
                    <input
                      type="file"
                      id="admin-avatar-upload"
                      accept="image/jpeg,image/jpg,image/png,image/gif"
                      onChange={handleFileInputChange}
                      style={{ display: 'none' }}
                    />
                    <button 
                      onClick={() => setShowAvatarOptions(!showAvatarOptions)}
                      className={`top-upload-button-small ${uploadingAvatar ? 'uploading' : ''}`}
                      disabled={uploadingAvatar}
                    >
                      {uploadingAvatar ? 'Uploading...' : 'Update Avatar'}
                    </button>
                    
                    {showAvatarOptions && (
                      <div className="avatar-options-dropdown">
                        <button 
                          className="avatar-option-button"
                          onClick={handleFileSelection}
                        >
                          Select Image
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="first_name">Name *</label>
                  <input
                    type="text"
                    id="first_name"
                    value={formData.first_name || ''}
                    onChange={(e) => handleFieldChange('first_name', e.target.value)}
                    className={validationErrors.first_name ? 'error' : ''}
                    placeholder="Enter first name"
                  />
                  {validationErrors.first_name && (
                    <span className="error-text">{validationErrors.first_name[0]}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="last_name">Surname *</label>
                  <input
                    type="text"
                    id="last_name"
                    value={formData.last_name || ''}
                    onChange={(e) => handleFieldChange('last_name', e.target.value)}
                    className={validationErrors.last_name ? 'error' : ''}
                    placeholder="Enter surname"
                  />
                  {validationErrors.last_name && (
                    <span className="error-text">{validationErrors.last_name[0]}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="preferred_name">Preferred Name</label>
                  <input
                    type="text"
                    id="preferred_name"
                    value={formData.preferred_name || ''}
                    onChange={(e) => handleFieldChange('preferred_name', e.target.value)}
                    placeholder="Enter preferred name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="short_name">Short Name</label>
                  <input
                    type="text"
                    id="short_name"
                    value={formData.short_name || ''}
                    onChange={(e) => handleFieldChange('short_name', e.target.value)}
                    placeholder="Enter short name or nickname"
                    maxLength={50}
                  />
                  <span className="field-help-text">Admin-assigned nickname (max 50 characters)</span>
                </div>

                <div className="form-group">
                  <label htmlFor="title">Title</label>
                  <input
                    type="text"
                    id="title"
                    value={formData.title || ''}
                    onChange={(e) => handleFieldChange('title', e.target.value)}
                    placeholder="e.g. Dr., Prof."
                    maxLength={100}
                  />
                  <span className="field-help-text">Display only — not used anywhere else</span>
                </div>

                <div className="form-group">
                  <label htmlFor="job_function">Function</label>
                  <input
                    type="text"
                    id="job_function"
                    value={formData.job_function || ''}
                    onChange={(e) => handleFieldChange('job_function', e.target.value)}
                    placeholder="Describe the employee's function"
                    maxLength={200}
                  />
                  <span className="field-help-text">Free text — display only</span>
                </div>

                <div className="form-group">
                  <AccountDatePicker
                    id="date_of_birth"
                    name="date_of_birth"
                    value={formData.dateOfBirth ? (
                      formData.dateOfBirth instanceof Date && !isNaN(formData.dateOfBirth.getTime()) 
                        ? `${formData.dateOfBirth.getDate().toString().padStart(2, '0')}/${(formData.dateOfBirth.getMonth() + 1).toString().padStart(2, '0')}/${formData.dateOfBirth.getFullYear()}`
                        : ''
                    ) : ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        try {
                          const parts = e.target.value.split('/');
                          if (parts.length === 3) {
                            // DD/MM/YYYY format with actual birth year
                            const day = parseInt(parts[0]);
                            const month = parseInt(parts[1]) - 1;
                            const year = parseInt(parts[2]);
                            const date = new Date(year, month, day);
                            
                            if (!isNaN(date.getTime()) && 
                                date.getDate() === day && 
                                date.getMonth() === month && 
                                date.getFullYear() === year) {
                              handleFieldChange('dateOfBirth', date);
                            } else {
                              handleFieldChange('dateOfBirth', null);
                            }
                          } else if (parts.length === 2) {
                            // Legacy DD/MM format support - use fixed year (2000)
                            const day = parseInt(parts[0]);
                            const month = parseInt(parts[1]) - 1;
                            const year = 2000;
                            const date = new Date(year, month, day);
                            
                            if (!isNaN(date.getTime()) && 
                                date.getDate() === day && 
                                date.getMonth() === month) {
                              handleFieldChange('dateOfBirth', date);
                            } else {
                              handleFieldChange('dateOfBirth', null);
                            }
                          } else {
                            handleFieldChange('dateOfBirth', null);
                          }
                        } catch (error) {
                          console.error('Birthday parsing error:', error);
                          handleFieldChange('dateOfBirth', null);
                        }
                      } else {
                        handleFieldChange('dateOfBirth', null);
                      }
                    }}
                    placeholder="Select birthday"
                    className={validationErrors.date_of_birth ? 'error' : ''}
                    format="DD/MM/YYYY"
                    label="Birthday"
                    required={false}
                  />
                  {validationErrors.date_of_birth && (
                    <span className="error-text">{validationErrors.date_of_birth[0]}</span>
                  )}
                </div>

                <div className="form-group">
                  <AccountDatePicker
                    id="date_joined_hms"
                    name="date_joined_hms"
                    value={formData.dateJoinedHMS ? (
                      formData.dateJoinedHMS instanceof Date && !isNaN(formData.dateJoinedHMS.getTime()) 
                        ? `${formData.dateJoinedHMS.getDate().toString().padStart(2, '0')}/${(formData.dateJoinedHMS.getMonth() + 1).toString().padStart(2, '0')}/${formData.dateJoinedHMS.getFullYear()}`
                        : ''
                    ) : ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        try {
                          const parts = e.target.value.split('/');
                          if (parts.length === 3) {
                            const day = parseInt(parts[0]);
                            const month = parseInt(parts[1]) - 1;
                            const year = parseInt(parts[2]);
                            const date = new Date(year, month, day);
                            
                            if (!isNaN(date.getTime()) && 
                                date.getDate() === day && 
                                date.getMonth() === month && 
                                date.getFullYear() === year) {
                              handleFieldChange('dateJoinedHMS', date);
                            } else {
                              handleFieldChange('dateJoinedHMS', null);
                            }
                          } else {
                            handleFieldChange('dateJoinedHMS', null);
                          }
                        } catch (error) {
                          console.error('Date joined HMS parsing error:', error);
                          handleFieldChange('dateJoinedHMS', null);
                        }
                      } else {
                        handleFieldChange('dateJoinedHMS', null);
                      }
                    }}
                    placeholder="Select when employee joined IMT"
                    className={validationErrors.date_joined_hms ? 'error' : ''}
                    format="DD/MM/YYYY"
                    label="Joined IMT"
                    required={false}
                  />
                  {validationErrors.date_joined_hms && (
                    <span className="error-text">{validationErrors.date_joined_hms[0]}</span>
                  )}
                </div>

                <div className="form-group">
                  <AccountDatePicker
                    id="end_date"
                    name="end_date"
                    value={formData.endDate ? (
                      formData.endDate instanceof Date && !isNaN(formData.endDate.getTime())
                        ? `${formData.endDate.getDate().toString().padStart(2, '0')}/${(formData.endDate.getMonth() + 1).toString().padStart(2, '0')}/${formData.endDate.getFullYear()}`
                        : ''
                    ) : ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        try {
                          const parts = e.target.value.split('/');
                          if (parts.length === 3) {
                            const day = parseInt(parts[0]);
                            const month = parseInt(parts[1]) - 1;
                            const year = parseInt(parts[2]);
                            const date = new Date(year, month, day);

                            if (!isNaN(date.getTime()) &&
                                date.getDate() === day &&
                                date.getMonth() === month &&
                                date.getFullYear() === year) {
                              handleFieldChange('endDate', date);
                            } else {
                              handleFieldChange('endDate', null);
                            }
                          } else {
                            handleFieldChange('endDate', null);
                          }
                        } catch (error) {
                          console.error('End date parsing error:', error);
                          handleFieldChange('endDate', null);
                        }
                      } else {
                        handleFieldChange('endDate', null);
                      }
                    }}
                    placeholder="Select end date"
                    className={validationErrors.end_date ? 'error' : ''}
                    format="DD/MM/YYYY"
                    label="End Date"
                    required={false}
                  />
                  <span style={{ fontSize: '0.8em', color: '#888' }}>
                    Access is removed automatically at the end of this date — offboarding starts.
                  </span>
                  {validationErrors.end_date && (
                    <span className="error-text">{validationErrors.end_date[0]}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Business Context Section */}
            <div className="form-section">
              <h2>Business Context</h2>
              <div className="business-type-checkbox-group">
                <label className="business-type-checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.businessContexts?.solidFuels || false}
                    onChange={(e) => handleFieldChange('businessContexts', {
                      ...formData.businessContexts,
                      solidFuels: e.target.checked
                    })}
                  />
                  <span>Solid fuels</span>
                </label>
                <label className="business-type-checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.businessContexts?.liquidFuels || false}
                    onChange={(e) => handleFieldChange('businessContexts', {
                      ...formData.businessContexts,
                      liquidFuels: e.target.checked
                    })}
                  />
                  <span>Liquid fuels</span>
                </label>
              </div>
            </div>

            {/* Team calendar consent (GDPR): HR can withdraw it for the person */}
            <div className="form-section">
              <h2>Team Calendar</h2>
              <div className="business-type-checkbox-group">
                <label className="business-type-checkbox-label">
                  <input
                    type="checkbox"
                    checked={Boolean(formData.team_calendar_consent)}
                    onChange={(e) => handleFieldChange('team_calendar_consent', e.target.checked)}
                  />
                  <span>{TEAM_CALENDAR_CONSENT_TEXT}</span>
                </label>
              </div>
            </div>

            {/* Organization Section */}
            <div className="form-section">
              <h2>Organization</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="office">Main Entity</label>
                  <select
                    id="office"
                    value={formData.office?.id || ''}
                    onChange={(e) => {
                      const selectedOffice = dropdownOptions.offices?.find(o => o.id === parseInt(e.target.value));
                      handleFieldChange('office', selectedOffice || null);
                      if (selectedOffice) {
                        const currentAdditional = formData.additional_offices || [];
                        const filtered = currentAdditional.filter(o => o.id !== selectedOffice.id);
                        if (filtered.length !== currentAdditional.length) {
                          handleFieldChange('additional_offices', filtered);
                        }
                      }
                    }}
                    className={validationErrors.office_id ? 'error' : ''}
                  >
                    <option value="">Select Main Entity</option>
                    {dropdownOptions.offices?.map(office => (
                      <option key={office.id} value={office.id}>
                        {office.name}
                      </option>
                    ))}
                  </select>
                  {validationErrors.office_id && (
                    <span className="error-text">{validationErrors.office_id[0]}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Additional Entities</label>
                  <div className="additional-entities-select" ref={additionalOfficesRef}>
                    <div
                      className={`additional-entities-select-header ${showAdditionalOfficesDropdown ? 'active' : ''} ${(!formData.additional_offices || formData.additional_offices.length === 0) ? 'placeholder' : ''}`}
                      onClick={() => setShowAdditionalOfficesDropdown(!showAdditionalOfficesDropdown)}
                    >
                      <span>
                        {!formData.additional_offices || formData.additional_offices.length === 0
                          ? 'Select additional entities'
                          : `${formData.additional_offices.length} additional ${formData.additional_offices.length === 1 ? 'entity' : 'entities'} selected`}
                      </span>
                    </div>

                    {showAdditionalOfficesDropdown && (() => {
                      const availableOffices = dropdownOptions.offices?.filter(o => o.id !== formData.office?.id) || [];
                      return (
                        <div className="additional-entities-dropdown">
                          {availableOffices.length === 0 ? (
                            <div className="additional-entities-option empty">
                              <span>No other entities available</span>
                            </div>
                          ) : (
                            availableOffices.map(office => {
                              const selected = (formData.additional_offices || []).some(ao => ao.id === office.id);
                              return (
                                <label key={office.id} className={`additional-entities-option ${selected ? 'selected' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={(e) => {
                                      const current = formData.additional_offices || [];
                                      const updated = e.target.checked
                                        ? [...current, office]
                                        : current.filter(ao => ao.id !== office.id);
                                      handleFieldChange('additional_offices', updated);
                                    }}
                                  />
                                  <span className="ae-checkbox" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="5 12 10 17 19 7" />
                                    </svg>
                                  </span>
                                  <span className="ae-label">{office.name}</span>
                                </label>
                              );
                            })
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  <div
                    className={`additional-entities-tags ${(!formData.additional_offices || formData.additional_offices.length === 0) ? 'empty' : ''}`}
                  >
                    {(!formData.additional_offices || formData.additional_offices.length === 0) ? (
                      <span className="empty-placeholder">No additional entities selected</span>
                    ) : (
                      formData.additional_offices.map(office => (
                        <span key={office.id} className="additional-entity-chip">
                          {office.name}
                          <button
                            type="button"
                            onClick={() => {
                              const updated = (formData.additional_offices || []).filter(ao => ao.id !== office.id);
                              handleFieldChange('additional_offices', updated);
                            }}
                            className="chip-remove"
                            aria-label={`Remove ${office.name}`}
                          >
                            ×
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="department">Department</label>
                  <select
                    id="department"
                    value={formData.department?.id || ''}
                    onChange={(e) => {
                      const selectedDept = dropdownOptions.departments?.find(d => d.id === parseInt(e.target.value));
                      handleFieldChange('department', selectedDept || null);
                    }}
                    className={validationErrors.department_id ? 'error' : ''}
                  >
                    <option value="">Select Department</option>
                    {dropdownOptions.departments?.map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                  {validationErrors.department_id && (
                    <span className="error-text">{validationErrors.department_id[0]}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="position">Position</label>
                  <select
                    id="position"
                    value={formData.position?.code || ''}
                    onChange={(e) => {
                      const selectedPosition = dropdownOptions.positions?.find(pos => pos.value === e.target.value);
                      if (selectedPosition) {
                        // Store as object with id, code, and name
                        handleFieldChange('position', {
                          id: selectedPosition.id,
                          code: selectedPosition.value,
                          name: selectedPosition.label
                        });
                      } else {
                        handleFieldChange('position', null);
                      }
                    }}
                    className={validationErrors.position ? 'error' : ''}
                  >
                    <option value="">Select Position</option>
                    {dropdownOptions.positions?.map(pos => (
                      <option key={pos.value} value={pos.value}>
                        {pos.label}
                      </option>
                    ))}
                  </select>
                  {validationErrors.position && (
                    <span className="error-text">{validationErrors.position[0]}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="location">Location</label>
                  <select
                    id="location"
                    value={formData.location || ''}
                    onChange={(e) => handleFieldChange('location', e.target.value)}
                    className={validationErrors.location ? 'error' : ''}
                  >
                    <option value="">Select Location</option>
                    {COUNTRIES.map(country => (
                      <option key={country.value} value={country.value}>
                        {country.label}
                      </option>
                    ))}
                  </select>
                  {validationErrors.location && (
                    <span className="error-text">{validationErrors.location[0]}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="supervisors">Supervisors</label>
                  <textarea
                    id="supervisors"
                    value={
                      formData.supervisors && formData.supervisors.length > 0
                        ? formData.supervisors.map(sup => sup.name).join(', ')
                        : 'Not set'
                    }
                    disabled
                    className="disabled-input"
                    rows={2}
                    style={{ resize: 'none' }}
                  />
                  <span className="field-help-text">This field can only be set via role assignment in admin dashboard</span>
                </div>

                <div className="form-group">
                  <label htmlFor="heads_of_department">Heads of Department</label>
                  <textarea
                    id="heads_of_department"
                    value={
                      formData.heads_of_department && formData.heads_of_department.length > 0
                        ? formData.heads_of_department.map(hod => hod.name).join(', ')
                        : 'Not set'
                    }
                    disabled
                    className="disabled-input"
                    rows={2}
                    style={{ resize: 'none' }}
                  />
                  <span className="field-help-text">This field can only be set via role assignment in admin dashboard</span>
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div className="form-section">
              <h2>Contact Information</h2>
              <div className="form-grid">
                <div className="form-group">
                  <PhoneField
                    id="work_phone"
                    label="Work Phone"
                    value={formData.work_phone}
                    onChange={(v) => handleFieldChange('work_phone', v)}
                    error={validationErrors.work_phone && validationErrors.work_phone[0]}
                    placeholder="Enter work phone number"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="work_email">Work Email</label>
                  <input
                    type="email"
                    id="work_email"
                    value={formData.work_email || formData.email || ''}
                    onChange={(e) => handleFieldChange('work_email', e.target.value)}
                    className={validationErrors.work_email ? 'error' : ''}
                    placeholder="Enter work email address"
                  />
                  {validationErrors.work_email && (
                    <span className="error-text">{validationErrors.work_email[0]}</span>
                  )}
                </div>

                <div className="form-group">
                  <PhoneField
                    id="personal_phone"
                    label="Personal Phone"
                    value={formData.personal_phone}
                    onChange={(v) => handleFieldChange('personal_phone', v)}
                    error={validationErrors.personal_phone && validationErrors.personal_phone[0]}
                    placeholder="Enter personal phone number"
                  />
                </div>
              </div>
            </div>

            {/* Languages Section */}
            <div className="form-section">
              <h2>Languages Spoken</h2>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label className="form-label">Select Languages</label>
                  <div className="additional-entities-select" ref={languagesRef}>
                    <div
                      className={`additional-entities-select-header ${showLanguagesDropdown ? 'active' : ''} ${(!formData.languagesSpoken || formData.languagesSpoken.length === 0) ? 'placeholder' : ''}`}
                      onClick={() => {
                        const next = !showLanguagesDropdown;
                        setShowLanguagesDropdown(next);
                        if (!next) setLanguageSearch('');
                      }}
                    >
                      <span>
                        {!formData.languagesSpoken || formData.languagesSpoken.length === 0
                          ? 'Select languages spoken'
                          : `${formData.languagesSpoken.length} language${formData.languagesSpoken.length > 1 ? 's' : ''} selected`}
                      </span>
                    </div>

                    {showLanguagesDropdown && (() => {
                      const q = languageSearch.trim().toLowerCase();
                      const filteredLanguages = (dropdownOptions.languages || []).filter(lang => {
                        if (!q) return true;
                        return (
                          (lang.label || '').toLowerCase().includes(q) ||
                          (lang.value || '').toLowerCase().includes(q)
                        );
                      });
                      return (
                        <div className="additional-entities-dropdown">
                          <div className="additional-entities-search">
                            <input
                              type="text"
                              value={languageSearch}
                              onChange={(e) => setLanguageSearch(e.target.value)}
                              placeholder="Search languages..."
                              autoFocus
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          {filteredLanguages.length === 0 ? (
                            <div className="no-results">No languages match "{languageSearch}"</div>
                          ) : (
                            filteredLanguages.map(lang => {
                              const selected = formData.languagesSpoken?.includes(lang.value) || false;
                              return (
                                <label key={lang.value} className={`additional-entities-option ${selected ? 'selected' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={(e) => {
                                      const current = formData.languagesSpoken || [];
                                      const updated = e.target.checked
                                        ? [...current, lang.value]
                                        : current.filter(l => l !== lang.value);
                                      handleLanguagesChange(updated);
                                    }}
                                  />
                                  <span className="ae-checkbox" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="5 12 10 17 19 7" />
                                    </svg>
                                  </span>
                                  <span className="ae-label">{lang.label}</span>
                                </label>
                              );
                            })
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  <div
                    className={`additional-entities-tags ${(!formData.languagesSpoken || formData.languagesSpoken.length === 0) ? 'empty' : ''}`}
                  >
                    {(!formData.languagesSpoken || formData.languagesSpoken.length === 0) ? (
                      <span className="empty-placeholder">No languages selected</span>
                    ) : (
                      formData.languagesSpoken.map(langValue => {
                        const lang = dropdownOptions.languages?.find(l => l.value === langValue);
                        if (!lang) return null;
                        return (
                          <span key={langValue} className="additional-entity-chip">
                            {lang.label}
                            <button
                              type="button"
                              onClick={() => {
                                const updated = (formData.languagesSpoken || []).filter(l => l !== langValue);
                                handleLanguagesChange(updated);
                              }}
                              className="chip-remove"
                              aria-label={`Remove ${lang.label}`}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Working With Me Guide Section */}
            <div className="form-section">
              <h2>Working With Me Guide</h2>
              <p className="section-description">
                Select characteristics that describe working style and communication preferences.
              </p>
              
              <div className="working-characteristics">
                <h3>Working Characteristics</h3>
                <div className="characteristics-grid">
                  {dropdownOptions.workingCharacteristics?.map(char => (
                    <label key={char.value} className="working-characteristics-checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.workingCharacteristics?.includes(char.value) || false}
                        onChange={(e) => handleWorkingCharacteristicsChange(char.value, e.target.checked)}
                        disabled={!formData.workingCharacteristics?.includes(char.value) && formData.workingCharacteristics?.length >= 10}
                      />
                      <span>{char.label}</span>
                    </label>
                  ))}
                </div>
                {validationErrors.working_characteristics && (
                  <span className="error-text">{validationErrors.working_characteristics[0]}</span>
                )}
              </div>

              <div className="custom-guide">
                <h3>Custom Working Guide</h3>
                <textarea
                  id="working_guide_custom"
                  value={formData.working_guide_custom || ''}
                  onChange={(e) => handleFieldChange('working_guide_custom', e.target.value)}
                  placeholder="Additional information about working style, communication preferences, time zones, etc..."
                  rows={4}
                  maxLength={500}
                />
                <div className={`character-count ${(formData.working_guide_custom || '').length > 450 ? 'approaching-limit' : ''}`}>
                  {(formData.working_guide_custom || '').length}/500 characters
                </div>
              </div>
            </div>

            {/* CRM Partner (Go-live 1 November): linked partner, or create one from this record */}
            <div className="form-section">
              <h2>CRM Partner</h2>
              <p className="section-description">
                The CRM partner record for this employee. Create Partner saves straight away; it does
                not wait for Save Changes.
              </p>
              <EmployeeCrmPartnerBlock employeeId={id} />
            </div>
          </div>
        </div>
        </div>
        
        {/* Hamster Chat Integration */}
        <HamsterFloatingButton />
        
        {/* Back to Top Button */}
        <BackToTopButton />
        
        {/* HMS Footer */}
        <Footer />
      </div>
    </Layout>
  );
}

export default withAuth(AdminEditEmployee);
